import { Box, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import GenericFilters from '@/components/GenericFilters';
import RangeDatePicker from '@/components/RangeDatePicker/RangeDatePicker';

import type { IFilters } from '../TableSection/useMyShipments';

import { filters } from '../utils';

type FiltersProps = {
  clearFilters: () => void;
  applyFilters: () => void;
  selectedFilters: IFilters;
  onChange: (filterName: keyof IFilters, value: string) => void;
};

const Filters = ({ selectedFilters, onChange, clearFilters, applyFilters }: FiltersProps) => {
  const theme = useTheme();
  const { t } = useTranslation('myShipments');

  const commonPaperStyle = {
    p: 2.5,
    mb: 2,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'background.paper',
  };

  const adaptedFilters: Record<string, string | string[]> = {
    deliveryType: selectedFilters.deliveryType ?? '',
    statusFilter: selectedFilters.statusFilter ?? '',
    platform: selectedFilters.platform ?? '',
    startDate: selectedFilters.startDate ?? '',
    endDate: selectedFilters.endDate ?? '',
  };

  const handleFilterChange = (name: string, value: string) => {
    onChange(name as keyof IFilters, value);
  };

  return (
    <Box sx={{ mt: 2, maxWidth: '100%', overflow: 'hidden' }}>
      <Paper elevation={0} sx={commonPaperStyle}>
        <GenericFilters
          filters={filters}
          selectedFilters={adaptedFilters}
          handleOnChange={handleFilterChange}
          typeCheck="radio"
        />
      </Paper>

      <Paper elevation={0} sx={commonPaperStyle}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          {t('FILTER_SECTION.SUBTITLE_DATE')}
        </Typography>
        <RangeDatePicker
          startLabel={t('FILTER_SECTION.START_DATE_LABEL')}
          endLabel={t('FILTER_SECTION.END_DATE_LABEL')}
          onChange={handleFilterChange}
          startValue={selectedFilters.startDate ?? ''}
          endValue={selectedFilters.endDate ?? ''}
        />
      </Paper>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          '& > button': {
            minWidth: '140px',
            px: 3,
            py: 1,
            [theme.breakpoints.down('sm')]: { width: '100%' },
          },
        }}
      >
        <Button onClick={clearFilters} variant="outlined">
          {t('FILTER_SECTION.BUTTONS.CLEAR')}
        </Button>
        <Button onClick={applyFilters} variant="contained">
          {t('FILTER_SECTION.BUTTONS.APPLY')}
        </Button>
      </Box>
    </Box>
  );
};

export default Filters;
